import React, { useContext, useState, forwardRef, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../utils/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';
import { sanitizeInput, logSecurityEvent, SecurityErrorBoundary } from '../../utils/security';
import { getProgramTabLabel } from '../../utils/programUtils';
import { getFieldValidationRules } from '../../utils/registrationFormUtils';

const PhoneInput = forwardRef(({ value = '', onChange, onBlur, name }, ref) => {
  const formatPhone = (input) => {
    const digits = input.replace(/\D/g, '');
    const normalizedDigits = digits.startsWith('7')
      ? digits
      : digits.length > 0
        ? '7' + digits.substring(digits.startsWith('8') ? 1 : 0)
        : '';

    let formatted = '';
    if (normalizedDigits.length > 0) {
      formatted = '+7';

      if (normalizedDigits.length > 1) {
        formatted += ' (';
        formatted += normalizedDigits.substring(1, Math.min(4, normalizedDigits.length));
      }

      if (normalizedDigits.length > 4) {
        formatted += ') ';
        formatted += normalizedDigits.substring(4, Math.min(7, normalizedDigits.length));
      }

      if (normalizedDigits.length > 7) {
        formatted += '-';
        formatted += normalizedDigits.substring(7, Math.min(9, normalizedDigits.length));
      }

      if (normalizedDigits.length > 9) {
        formatted += '-';
        formatted += normalizedDigits.substring(9, Math.min(11, normalizedDigits.length));
      }
    }

    return formatted;
  };

  const handleChange = (e) => {
    onChange(formatPhone(e.target.value));
  };

  return (
    <input
      ref={ref}
      type="tel"
      inputMode="numeric"
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      placeholder="+7 (___) ___-__-__"
      maxLength={18}
    />
  );
});

PhoneInput.displayName = 'PhoneInput';

const FieldLabel = ({ children, required = false }) => (
  <label>
    {children}
    {required && <span className="required-mark" aria-hidden="true">*</span>}
  </label>
);

const CloseButton = ({ onClose }) => (
  <motion.button
    type="button"
    className="btn-close"
    onClick={onClose}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    aria-label="Закрыть"
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M6 6l12 12M18 6L6 18"
      />
    </svg>
  </motion.button>
);

const RegistrationField = ({ field, register, control, errors, forums, forumsLoading }) => {
  const rules = getFieldValidationRules(field);
  const error = errors[field.key];

  if (field.type === 'phone') {
    return (
      <motion.div className="form-group">
        <FieldLabel required={field.required}>{field.label}</FieldLabel>
        <Controller
          name={field.key}
          control={control}
          rules={rules}
          render={({ field: inputField }) => <PhoneInput {...inputField} />}
        />
        {error && <span className="error">{error.message}</span>}
      </motion.div>
    );
  }

  if (field.type === 'select') {
    return (
      <motion.div className="form-group">
        <FieldLabel required={field.required}>{field.label}</FieldLabel>
        <select
          {...register(field.key, rules)}
          className="region-select"
        >
          <option value="">{field.placeholder || `Выберите ${field.label.toLowerCase()}`}</option>
          {(field.options || []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {error && <span className="error">{error.message}</span>}
      </motion.div>
    );
  }

  if (field.type === 'forum') {
    return (
      <motion.div className="form-group">
        <FieldLabel required={field.required}>{field.label}</FieldLabel>
        <select
          {...register(field.key, rules)}
          className="region-select"
          disabled={forumsLoading || forums.length === 0}
        >
          <option value="">
            {forumsLoading ? 'Загрузка...' : (field.placeholder || `Выберите ${field.label.toLowerCase()}`)}
          </option>
          {forums.map((forum, index) => (
            <option key={forum.id} value={forum.id}>
              {getProgramTabLabel(forum, index)}
            </option>
          ))}
        </select>
        {!forumsLoading && forums.length === 0 && (
          <span className="error">Список форумов пока недоступен</span>
        )}
        {error && <span className="error">{error.message}</span>}
      </motion.div>
    );
  }

  return (
    <motion.div className="form-group">
      <FieldLabel required={field.required}>{field.label}</FieldLabel>
      <input
        type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
        autoComplete={field.type === 'email' ? 'email' : undefined}
        placeholder={field.placeholder || undefined}
        {...register(field.key, rules)}
      />
      {error && <span className="error">{error.message}</span>}
    </motion.div>
  );
};

const Registration = () => {
  const { isRegistrationCompleted } = useContext(AuthContext);
  const { register, handleSubmit, formState: { errors }, control, setValue } = useForm();
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forums, setForums] = useState([]);
  const [forumsLoading, setForumsLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState('');
  const [programsEnabled, setProgramsEnabled] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/programs')
      .then((response) => {
        const sections = (response.data?.sections || []).filter((section) => section.id);
        setForums(sections);
        setProgramsEnabled(response.data?.isEnabled !== false);
      })
      .catch((error) => {
        console.error('Error loading forums:', error);
        setForums([]);
        setProgramsEnabled(true);
      })
      .finally(() => {
        setForumsLoading(false);
      });
  }, []);

  useEffect(() => {
    axios.get('/api/registration-form')
      .then((response) => {
        setFields(response.data?.fields || []);
        setFieldsError('');
      })
      .catch((error) => {
        console.error('Error loading registration form:', error);
        setFields([]);
        setFieldsError('Не удалось загрузить форму регистрации');
      })
      .finally(() => {
        setFieldsLoading(false);
      });
  }, []);

  useEffect(() => {
    const forumField = fields.find((field) => field.type === 'forum');
    if (programsEnabled && forumField && forums.length === 1) {
      setValue(forumField.key, String(forums[0].id));
    }
  }, [fields, forums, programsEnabled, setValue]);

  const closeRegistration = () => {
    navigate('/', { replace: true });
  };

  const onSubmit = async (data) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const visibleFields = programsEnabled
        ? fields
        : fields.filter((field) => field.type !== 'forum');

      const formattedData = {};
      visibleFields.forEach((field) => {
        const rawValue = data[field.key];
        if (field.type === 'forum') {
          formattedData[field.key] = Number.parseInt(rawValue, 10);
          return;
        }

        const value = sanitizeInput(String(rawValue ?? '')).trim();
        formattedData[field.key] = field.type === 'email' ? value.toLowerCase() : value;
      });

      const response = await axios.post('/register', formattedData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        logSecurityEvent('REGISTRATION_SUCCESS', { email: formattedData.email });
        setPopupMessage('Регистрация прошла успешно!');
        setPopupVisible(true);

        setTimeout(() => {
          closeRegistration();
        }, 2000);
      } else {
        throw new Error('Неожиданный ответ от сервера');
      }
    } catch (error) {
      logSecurityEvent('REGISTRATION_ERROR', {
        error: error.message,
        email: data.email,
        response: error.response?.data
      });

      if (error.response?.status === 404) {
        setPopupMessage('Сервер недоступен. Пожалуйста, попробуйте позже.');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Проверьте правильность введенных данных';
        setPopupMessage(errorMessage);
      } else {
        setPopupMessage('Ошибка при регистрации. Пожалуйста, попробуйте снова.');
      }
      setPopupVisible(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = (e, filename) => {
    e.preventDefault();
    logSecurityEvent('FILE_DOWNLOAD_ATTEMPT', { file: filename });

    const link = document.createElement('a');
    link.href = `/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    logSecurityEvent('FILE_DOWNLOAD_SUCCESS', { file: filename });
  };

  const visibleFields = programsEnabled
    ? fields
    : fields.filter((field) => field.type !== 'forum');

  const formVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.25, ease: 'easeOut' }
    },
    exit: {
      opacity: 0,
      y: 12,
      transition: { duration: 0.2 }
    }
  };

  return (
    <SecurityErrorBoundary>
      <motion.div
        className="form"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeRegistration();
          }
        }}
      >
        <div
          className="form-container"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClose={closeRegistration} />
          {isRegistrationCompleted ? (
            <motion.h3
              className="form-title-completed"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              РЕГИСТРАЦИЯ ЗАВЕРШЕНА
            </motion.h3>
          ) : (
            <motion.form
              onSubmit={handleSubmit(onSubmit)}
              className="form-content"
            >
              {fieldsLoading ? (
                <p className="form-loading">Загрузка формы...</p>
              ) : fieldsError ? (
                <span className="error">{fieldsError}</span>
              ) : (
                visibleFields.map((field) => (
                  <RegistrationField
                    key={field.key}
                    field={field}
                    register={register}
                    control={control}
                    errors={errors}
                    forums={forums}
                    forumsLoading={forumsLoading}
                  />
                ))
              )}

              <div className="form-consents">
                <motion.div className="form-checkbox">
                  <input type="checkbox" name="privacy1" value="on" required />
                  <a
                    href="/soglasie1.docx"
                    onClick={(event) => handleDownload(event, 'soglasie1.docx')}
                    className="privacy-link"
                  >
                    <DownloadIcon fontSize="small" />
                    Согласие на обработку персональных данных
                  </a>
                </motion.div>
                <motion.div className="form-checkbox">
                  <input type="checkbox" name="privacy2" value="on" required />
                  <a
                    href="/soglasie2.docx"
                    onClick={(event) => handleDownload(event, 'soglasie2.docx')}
                    className="privacy-link"
                  >
                    <DownloadIcon fontSize="small" />
                    Согласие на передачу данных для обработки
                  </a>
                </motion.div>
                <motion.div className="form-checkbox">
                  <input type="checkbox" name="privacy3" value="on" required />
                  <a
                    href="/Soglasie3.docx"
                    onClick={(event) => handleDownload(event, 'Soglasie3.docx')}
                    className="privacy-link"
                  >
                    <DownloadIcon fontSize="small" />
                    Согласие на размещение и использование материалов
                  </a>
                </motion.div>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                disabled={isSubmitting || fieldsLoading || Boolean(fieldsError) || visibleFields.length === 0}
              >
                {isSubmitting ? 'Отправка...' : 'Отправить'}
              </motion.button>
            </motion.form>
          )}

          <AnimatePresence>
            {isPopupVisible && (
              <motion.div
                className="popup"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="popup-content"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <motion.span
                    className="close"
                    onClick={() => {
                      setPopupVisible(false);
                      closeRegistration();
                    }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    &times;
                  </motion.span>
                  <p>{popupMessage}</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </SecurityErrorBoundary>
  );
};

export default Registration;
